/**
 * MiloConsolePanel — Phase 5C admin Q&A console.
 *
 * Talks to the existing Phase 5A backend `POST /api/milo/ask` (admin-auth).
 * Read-only. No action affordances. Renders grounded answers with runbook
 * citations and dedicated copy for each refusal_reason. Citations are
 * clickable and deep-link to AI → Milo → Runbooks with the correct
 * runbook pre-selected.
 *
 * Inline CSS only · luxury admin styling · gold as accent only.
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  Terminal, Send, ScrollText, AlertCircle, Lock, Loader2,
  CheckCircle2, ShieldAlert, BookOpen, ArrowRight, Sparkles,
} from 'lucide-react';
import { adminAuthHeader } from '../../data/api';
import MiloThinking from './_shared/MiloThinking';

const API = process.env.REACT_APP_BACKEND_URL;
// Evaluated PER-REQUEST so the real admin token (set at login) is read, not a stale module-load value.
const ADMIN_AUTH = () => adminAuthHeader();

const GOLD = '#C6A75A';
const GOLD_DK = '#8E7A2C';
const DARK = '#1B1812';
const MUTED = '#5C5C66';
const BORDER = 'rgba(0,0,0,0.10)';
const BORDER_GOLD = 'rgba(198,167,90,0.32)';
const SURFACE = '#FFFFFF';
const SOFT_BG = 'rgba(0,0,0,0.025)';
const RED_DK = '#B23A48';
const GREEN_DK = '#1E7344';

const SCOPE_FILL = {
  dashboard: '#1E7344', guests: '#2C66B8', vendors: '#7A4FB6',
  finance: '#9D6F1F',  messaging: '#B23A48', settings: '#5C5C66',
  ai: GOLD_DK,         cross: '#3A4252',
};

const REFUSAL_TONE = {
  action_request: { icon: ShieldAlert, color: GOLD_DK, label: 'Read-only' },
  guest_scope:    { icon: ShieldAlert, color: GOLD_DK, label: 'Wrong assistant' },
  no_grounding:   { icon: BookOpen,    color: MUTED,   label: 'No matching runbook' },
  no_llm_key:     { icon: AlertCircle, color: RED_DK,  label: 'Service issue' },
  llm_error:      { icon: AlertCircle, color: RED_DK,  label: 'Service issue' },
  empty_question: { icon: AlertCircle, color: MUTED,   label: 'Empty question' },
};

const ScopePill = ({ scope }) => {
  const fill = SCOPE_FILL[scope] || GOLD_DK;
  return (
    <span style={{
      fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
      textTransform: 'uppercase', padding: '2px 7px', borderRadius: 99,
      background: `${fill}14`, color: fill, border: `1px solid ${fill}38`,
    }}>{scope || '?'}</span>
  );
};

const StatusBadge = ({ children, tone = 'gold' }) => {
  const palette = tone === 'gold'
    ? { bg: 'rgba(198,167,90,0.12)', fg: GOLD_DK, br: BORDER_GOLD }
    : tone === 'green'
    ? { bg: 'rgba(30,115,68,0.10)', fg: GREEN_DK, br: 'rgba(30,115,68,0.28)' }
    : tone === 'red'
    ? { bg: 'rgba(178,58,72,0.08)', fg: RED_DK, br: 'rgba(178,58,72,0.26)' }
    : { bg: 'rgba(0,0,0,0.05)', fg: MUTED, br: 'rgba(0,0,0,0.16)' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
      textTransform: 'uppercase', padding: '3px 9px', borderRadius: 99,
      background: palette.bg, color: palette.fg,
      border: `1px solid ${palette.br}`,
      display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {children}
    </span>
  );
};

// Lightweight Markdown-ish renderer for Milo's answer body so the citation
// trailer line `[Cited: …]` is rendered as a small footnote, headings get
// proper weight, and bullets read cleanly. Mirrors the runbooks renderer
// to stay visually consistent.
const AnswerBody = ({ text }) => {
  const lines = (text || '').split('\n');
  const blocks = [];
  let buf = [];
  let citedTrailer = null;

  const flush = () => {
    if (buf.length === 0) return;
    blocks.push({ kind: 'p', items: buf });
    buf = [];
  };

  for (const ln of lines) {
    const l = ln.trimEnd();
    if (l.startsWith('[Cited:') && l.endsWith(']')) { citedTrailer = l; continue; }
    if (l.startsWith('## '))  { flush(); blocks.push({ kind: 'h2', text: l.slice(3) }); continue; }
    if (l.startsWith('### ')) { flush(); blocks.push({ kind: 'h3', text: l.slice(4) }); continue; }
    if (l.startsWith('- ') || l.startsWith('* ')) { flush(); blocks.push({ kind: 'li', text: l.slice(2) }); continue; }
    if (l === '') { flush(); continue; }
    buf.push(l);
  }
  flush();

  const renderInline = (s) => {
    const parts = String(s).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) {
        return <strong key={i} style={{ color: DARK, fontWeight: 700 }}>{p.slice(2, -2)}</strong>;
      }
      if (p.startsWith('`') && p.endsWith('`')) {
        return <code key={i} style={{
          fontFamily: 'ui-monospace, Menlo, monospace',
          fontSize: 12, padding: '1px 5px', borderRadius: 5,
          background: SOFT_BG, color: DARK,
          border: `1px solid ${BORDER}`,
        }}>{p.slice(1, -1)}</code>;
      }
      return <span key={i}>{p}</span>;
    });
  };

  return (
    <div data-testid="milo-answer-body" style={{
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: 14, lineHeight: 1.7, color: DARK,
    }}>
      {blocks.map((b, i) => {
        if (b.kind === 'h2') return (
          <h4 key={i} style={{ margin: '14px 0 6px',
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 18, fontWeight: 700, color: DARK }}>
            {renderInline(b.text)}
          </h4>);
        if (b.kind === 'h3') return (
          <h5 key={i} style={{ margin: '12px 0 4px', fontSize: 13,
            fontWeight: 700, color: DARK }}>{renderInline(b.text)}</h5>);
        if (b.kind === 'li') return (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
            <span style={{ color: GOLD_DK, fontWeight: 700, marginTop: 1 }}>•</span>
            <span style={{ flex: 1 }}>{renderInline(b.text)}</span>
          </div>);
        return (
          <p key={i} style={{ margin: '0 0 10px' }}>
            {b.items.map((ln, j) => (
              <React.Fragment key={j}>
                {renderInline(ln)}
                {j < b.items.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>);
      })}
      {citedTrailer && (
        <div data-testid="milo-answer-cited-trailer"
          style={{ marginTop: 8, fontSize: 11, color: MUTED,
            fontStyle: 'italic', letterSpacing: '0.2px' }}>
          {citedTrailer}
        </div>
      )}
    </div>
  );
};

const SAMPLE_PROMPTS = [
  'What needs my attention right now?',
  'Add a guest, John Smith',
  'Prepare invitations for the Acosta party',
  'Where do I review pending RSVPs?',
  'Show me the messaging runbook',
];

const MiloConsolePanel = ({ onOpenRunbook, onNavigate }) => {
  const [question, setQuestion] = useState('');
  const [history, setHistory]   = useState([]); // { role: 'user'|'milo', payload }
  const [loading, setLoading]   = useState(false);
  const [grounding, setGrounding] = useState({ count: null });
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  // Pull the runbook count once for the header indicator.
  useEffect(() => {
    fetch(`${API}/api/runbooks`, { headers: ADMIN_AUTH() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setGrounding({ count: d.count || 0 }); })
      .catch(() => {});
  }, []);

  // Auto-scroll the conversation feed when a new turn lands.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, loading]);

  const ask = async (text) => {
    const q = (text ?? question).trim();
    if (!q || loading) return;
    setHistory(h => [...h, { role: 'user', text: q, ts: Date.now() }]);
    setQuestion('');
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/milo/ask`, {
        method: 'POST',
        headers: { ...ADMIN_AUTH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, session_id: 'milo-console' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setHistory(h => [...h, { role: 'milo', payload: d, ts: Date.now() }]);
    } catch (e) {
      setHistory(h => [...h, {
        role: 'milo',
        payload: {
          answer: `Sorry — I couldn't reach the Milo backend (${e.message || e}). I'd rather say so than guess.`,
          grounded: false,
          refusal_reason: 'llm_error',
          citations: [],
        },
        ts: Date.now(),
      }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); }
  };

  const handleCitationClick = (familyId) => {
    if (!familyId || !onOpenRunbook) return;
    onOpenRunbook(familyId);
  };

  // Phase 6A — proposal handlers ------------------------------------------
  const [dismissedProposalIds, setDismissedProposalIds] = useState(() => new Set());

  const handleProposalReview = (proposal) => {
    const t = proposal?.target;
    if (!t) return;
    if (t.kind === 'milo_runbook' && t.family_id) {
      // Reuse the same handoff used by citation chips (Phase 5C).
      if (onOpenRunbook) onOpenRunbook(t.family_id);
      return;
    }
    if (t.kind === 'tab' && t.tab && onNavigate) {
      onNavigate(t.tab);
      return;
    }
    // Phase 6B — L1 prefill handoff. One-shot sessionStorage carrier
    // (same pattern as Phase 3D / 5C). The receiving form reads the key
    // on mount, applies the prefill to its EXISTING state hooks, clears
    // the key, and shows a "Milo prepared this" banner. The existing
    // Send / Generate / template-edit buttons remain entirely the
    // operator's. Milo never submits anything.
    if (t.kind === 'messaging_compose_prefill' && onNavigate) {
      try {
        sessionStorage.setItem('milo_proposal_prefill', JSON.stringify({
          action_id: proposal.action_id,
          proposal_id: proposal.id,
          target: t,
          summary: proposal.summary,
          runbook_family_id: proposal.runbook_family_id,
          runbook_version: proposal.runbook_version,
          issued_at: Date.now(),
        }));
      } catch (_) {}
      onNavigate(t.tab || 'messaging', { subtab: t.subtab || 'compose' });
      return;
    }
    // Item 4 — L1 template-assist prefill. Routes to the Messaging →
    // Email subtab and asks it to preselect an approved template by id.
    // Same one-shot sessionStorage handoff as 6B, separate key so the
    // two prefills never collide if both are issued in close succession.
    // Receiving side will preselect the template and apply an optional
    // template-search filter; recipient checkboxes, subject, body, and
    // the Send button remain entirely operator-controlled.
    if (t.kind === 'messaging_email_template_prefill' && onNavigate) {
      try {
        sessionStorage.setItem('milo_email_template_prefill', JSON.stringify({
          action_id: proposal.action_id,
          proposal_id: proposal.id,
          target: t,
          summary: proposal.summary,
          runbook_family_id: proposal.runbook_family_id,
          runbook_version: proposal.runbook_version,
          issued_at: Date.now(),
        }));
      } catch (_) {}
      onNavigate(t.tab || 'messaging', { subtab: t.subtab || 'email' });
      return;
    }
    // Item 5 — L1 guests guided-intake prefill. Routes to the Guests
    // tab and asks GuestListTab to expand its existing Add Guest card
    // and seed the four-field form (first_name, last_name, email,
    // phone). Same one-shot sessionStorage handoff. Receiving side
    // never auto-clicks the existing Add Guest button — the operator
    // reviews and submits manually. No guest is created by Milo.
    if (t.kind === 'guests_add_guest_prefill' && onNavigate) {
      try {
        sessionStorage.setItem('milo_add_guest_prefill', JSON.stringify({
          action_id: proposal.action_id,
          proposal_id: proposal.id,
          target: t,
          summary: proposal.summary,
          runbook_family_id: proposal.runbook_family_id,
          runbook_version: proposal.runbook_version,
          issued_at: Date.now(),
        }));
      } catch (_) {}
      onNavigate(t.tab || 'guests', { subtab: t.subtab || null });
      return;
    }
  };

  const handleProposalDismiss = async (proposal) => {
    if (!proposal?.id) return;
    // Optimistically hide the card; persist the rejection in the background.
    setDismissedProposalIds(prev => {
      const next = new Set(prev);
      next.add(proposal.id);
      return next;
    });
    try {
      await fetch(`${API}/api/milo/proposals/${proposal.id}/dismiss`, {
        method: 'POST',
        headers: { ...ADMIN_AUTH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'operator_dismissed' }),
      });
    } catch (_) {
      // Even on network failure we keep the card hidden — the operator
      // already made their choice. Action Log will not double-log.
    }
  };
  // -------------------------------------------------------------------------

  const isEmpty = history.length === 0 && !loading;

  return (
    <div data-testid="milo-subtab-panel-console"
      style={{ border: `1px solid ${BORDER}`, borderRadius: 14,
        background: SURFACE, padding: '20px 22px',
        display: 'flex', flexDirection: 'column', gap: 14, minHeight: 600 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14,
        flexWrap: 'wrap' }}>
        <div style={{ width: 44, height: 44, borderRadius: 11,
          background: 'rgba(198,167,90,0.10)',
          border: `1px solid ${BORDER_GOLD}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0 }}>
          <Terminal size={20} color={GOLD_DK} strokeWidth={1.6} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
            flexWrap: 'wrap', marginBottom: 4 }}>
            <h3 style={{ margin: 0,
              fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 22, fontWeight: 700, color: DARK,
              letterSpacing: '0.2px' }}>
              Milo Console
            </h3>
            <StatusBadge tone="gold">Phase 5C · Live</StatusBadge>
            <StatusBadge tone="muted">Read-only · admin-only</StatusBadge>
            {grounding.count !== null && (
              <StatusBadge tone="muted">
                Grounded in {grounding.count} runbook{grounding.count === 1 ? '' : 's'}
              </StatusBadge>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.55,
            maxWidth: 720 }}>
            Ask operational questions about the admin app. Milo answers
            <strong style={{ color: DARK }}> only </strong>
            from the runbooks authored under
            <strong style={{ color: DARK }}> Runbooks </strong>
            (the sibling tab) and cannot take actions on your behalf.
          </p>
        </div>
      </div>

      {/* Conversation feed */}
      <div ref={scrollRef} data-testid="milo-console-feed"
        style={{
          flex: 1, minHeight: 320, maxHeight: 540, overflowY: 'auto',
          border: `1px solid ${BORDER}`, borderRadius: 12,
          background: SOFT_BG, padding: '18px 18px 14px',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}>

        {isEmpty && (
          <div data-testid="milo-console-empty"
            style={{ flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 14,
              padding: '32px 20px', color: MUTED }}>
            <div style={{ width: 56, height: 56, borderRadius: 14,
              background: 'rgba(198,167,90,0.10)',
              border: `1px solid ${BORDER_GOLD}`,
              display: 'flex', alignItems: 'center',
              justifyContent: 'center' }}>
              <Sparkles size={26} color={GOLD_DK} strokeWidth={1.5} />
            </div>
            <div style={{ textAlign: 'center', maxWidth: 460 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: DARK,
                marginBottom: 4 }}>
                Ask Milo about how the admin app works
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                Every answer is grounded in the operator runbooks. If
                nothing matches, Milo will say so plainly.
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6,
              justifyContent: 'center', maxWidth: 620 }}>
              {SAMPLE_PROMPTS.map((p, i) => (
                <button key={i}
                  data-testid={`milo-console-sample-${i}`}
                  onClick={() => ask(p)}
                  style={{ padding: '6px 12px', borderRadius: 99,
                    border: `1px solid ${BORDER_GOLD}`,
                    background: '#FFFFFF', color: GOLD_DK,
                    fontSize: 11, fontWeight: 600,
                    letterSpacing: '0.2px', cursor: 'pointer',
                    transition: 'all 0.15s ease' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(198,167,90,0.06)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#FFFFFF';
                  }}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {history.map((turn, i) => (
          <ConversationTurn key={i} turn={turn}
            onCitationClick={handleCitationClick}
            onProposalReview={handleProposalReview}
            onProposalDismiss={handleProposalDismiss}
            dismissedProposalIds={dismissedProposalIds} />
        ))}

        {loading && (
          <div data-testid="milo-console-loading"
            style={{ alignSelf: 'flex-start', maxWidth: '78%',
              padding: '12px 16px', borderRadius: 14,
              borderTopLeftRadius: 4,
              background: '#FFFFFF',
              border: `1px solid ${BORDER}` }}>
            <MiloThinking label="Milo is reading the runbooks…" />
          </div>
        )}
      </div>

      {/* Input row */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input ref={inputRef}
            data-testid="milo-console-input"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask an admin question — e.g. 'How is liquidity_gap calculated?'"
            disabled={loading}
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 10,
              border: `1px solid ${BORDER}`, background: '#FFFFFF',
              fontSize: 13, color: DARK, outline: 'none',
              opacity: loading ? 0.6 : 1, transition: 'border-color 0.15s ease',
            }}
            onFocus={e => { e.currentTarget.style.borderColor = GOLD; }}
            onBlur={e  => { e.currentTarget.style.borderColor = BORDER; }} />
        </div>
        <button data-testid="milo-console-ask-btn"
          onClick={() => ask()}
          disabled={loading || !question.trim()}
          style={{ padding: '0 18px', borderRadius: 10, border: 'none',
            background: loading || !question.trim()
              ? 'rgba(0,0,0,0.08)'
              : `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`,
            color: loading || !question.trim() ? MUTED : '#1B1812',
            fontWeight: 800, fontSize: 12, letterSpacing: '0.6px',
            textTransform: 'uppercase',
            cursor: loading || !question.trim() ? 'not-allowed' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            boxShadow: loading || !question.trim()
              ? 'none' : '0 2px 10px rgba(198,167,90,0.30)',
            transition: 'all 0.15s ease',
          }}>
          {loading ? <Loader2 size={13}
            style={{ animation: 'milo-spin 0.9s linear infinite' }} />
            : <Send size={13} />}
          {loading ? 'Asking…' : 'Ask Milo'}
        </button>
      </div>

      {/* Read-only boundary footer */}
      <div data-testid="milo-console-boundary-note"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '11px 14px', borderRadius: 10,
          background: 'rgba(198,167,90,0.05)',
          border: `1px solid ${BORDER_GOLD}` }}>
        <Lock size={12} color={GOLD_DK}
          style={{ marginTop: 1, flexShrink: 0 }} />
        <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.55 }}>
          Milo is the operator-side counterpart to Oscar. He is
          <strong style={{ color: DARK }}> read-only</strong>,
          <strong style={{ color: DARK }}> not </strong>
          guest-facing, and cannot trigger workflows, send messages, or
          mutate data. Citations link back to the source runbook so you
          can verify the grounding yourself.
        </div>
      </div>

      <style>{`
        @keyframes milo-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Single user/milo turn rendering — a small chat-bubble pair plus the
// citation chips and refusal banner where applicable.
// ──────────────────────────────────────────────────────────────────────────
const ConversationTurn = ({ turn, onCitationClick,
  onProposalReview, onProposalDismiss, dismissedProposalIds }) => {
  if (turn.role === 'user') {
    return (
      <div data-testid="milo-turn-user" style={{ alignSelf: 'flex-end',
        maxWidth: '78%', padding: '10px 14px', borderRadius: 14,
        borderTopRightRadius: 4,
        background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`,
        color: '#1B1812', fontSize: 13, lineHeight: 1.55,
        boxShadow: '0 2px 10px rgba(198,167,90,0.20)',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {turn.text}
      </div>
    );
  }

  const p = turn.payload || {};
  const tone = REFUSAL_TONE[p.refusal_reason] || null;
  const RefIcon = tone?.icon;

  return (
    <div data-testid="milo-turn-milo"
      style={{ alignSelf: 'flex-start', maxWidth: '88%',
        padding: '14px 16px', borderRadius: 14,
        borderTopLeftRadius: 4,
        background: '#FFFFFF',
        border: p.grounded ? `1px solid ${BORDER}` : `1px solid ${BORDER_GOLD}`,
        boxShadow: '0 1px 3px rgba(20,15,5,0.04)',
        display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* Header strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8,
        flexWrap: 'wrap', fontSize: 10, color: MUTED }}>
        {p.grounded ? (
          <span data-testid="milo-turn-grounded"
            style={{ display: 'inline-flex', alignItems: 'center',
              gap: 5, fontSize: 10, fontWeight: 700,
              letterSpacing: '0.6px', textTransform: 'uppercase',
              padding: '3px 9px', borderRadius: 99,
              background: 'rgba(30,115,68,0.08)', color: GREEN_DK,
              border: '1px solid rgba(30,115,68,0.26)' }}>
            <CheckCircle2 size={11} /> Grounded
          </span>
        ) : tone ? (
          <span data-testid="milo-turn-refusal"
            style={{ display: 'inline-flex', alignItems: 'center',
              gap: 5, fontSize: 10, fontWeight: 700,
              letterSpacing: '0.6px', textTransform: 'uppercase',
              padding: '3px 9px', borderRadius: 99,
              background: `${tone.color}14`, color: tone.color,
              border: `1px solid ${tone.color}38` }}>
            {RefIcon && <RefIcon size={11} />} {tone.label}
          </span>
        ) : null}
        {typeof p.latency_ms === 'number' && p.latency_ms > 0 && (
          <span style={{ fontSize: 10, color: MUTED }}>
            {p.latency_ms} ms
          </span>
        )}
        {p.model && (
          <span style={{ fontSize: 10, color: MUTED, fontFamily: 'ui-monospace, Menlo, monospace' }}>
            {p.model}
          </span>
        )}
      </div>

      {/* Answer body */}
      <AnswerBody text={p.answer || ''} />

      {/* Citations row */}
      {(p.citations || []).length > 0 && (
        <div data-testid="milo-turn-citations"
          style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 10,
            display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 800,
            letterSpacing: '0.10em', textTransform: 'uppercase',
            color: MUTED }}>
            Cited runbooks
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {p.citations.map((c, i) => (
              <button key={(c.family_id || '') + i}
                data-testid={`milo-citation-${c.scope || 'unk'}`}
                onClick={() => onCitationClick(c.family_id)}
                title={`Open ${c.title} in Runbooks`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '6px 10px', borderRadius: 9,
                  border: `1px solid ${BORDER}`, background: '#FFFFFF',
                  cursor: 'pointer', maxWidth: 360, transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = GOLD;
                  e.currentTarget.style.background = 'rgba(198,167,90,0.04)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = BORDER;
                  e.currentTarget.style.background = '#FFFFFF';
                }}>
                <ScopePill scope={c.scope} />
                <span style={{ fontSize: 12, fontWeight: 600, color: DARK,
                  whiteSpace: 'nowrap', overflow: 'hidden',
                  textOverflow: 'ellipsis', maxWidth: 220 }}>
                  {c.title}
                </span>
                <span style={{ fontSize: 10, color: MUTED,
                  letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  v{c.version}
                </span>
                <ArrowRight size={11} color={GOLD_DK} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Phase 6A — Proposal cards (L0 navigation only) ─────────────── */}
      {(p.proposals || []).filter(pr => !(dismissedProposalIds?.has?.(pr.id))).map((pr, i) => (
        <div key={pr.id || i}
          data-testid={`milo-proposal-card-${pr.action_id}`}
          style={{
            border: `1px solid ${BORDER_GOLD}`, borderRadius: 11,
            background: 'rgba(198,167,90,0.04)',
            padding: '12px 14px',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
            flexWrap: 'wrap' }}>
            <span data-testid="milo-proposal-tier-pill"
              style={{ fontSize: 9, fontWeight: 800,
                letterSpacing: '0.10em', textTransform: 'uppercase',
                padding: '3px 8px', borderRadius: 99,
                background: 'rgba(198,167,90,0.14)',
                color: GOLD_DK, border: `1px solid ${BORDER_GOLD}` }}>
              {pr.tier} · Suggestion
            </span>
            <span style={{ fontSize: 9, fontWeight: 700,
              letterSpacing: '0.04em', color: MUTED,
              textTransform: 'uppercase' }}>
              {pr.action_id}
            </span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: DARK,
            lineHeight: 1.5 }}>
            {pr.summary}
          </div>
          {pr.reasoning && (
            <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.55,
              fontStyle: 'italic' }}>
              {pr.reasoning}
            </div>
          )}
          <div style={{ fontSize: 10, color: MUTED, lineHeight: 1.5 }}>
            Milo will not perform this — clicking
            <strong style={{ color: DARK }}> Review </strong>
            simply navigates you to the destination.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
            marginTop: 2 }}>
            <button data-testid={`milo-proposal-review-${pr.action_id}`}
              onClick={() => onProposalReview && onProposalReview(pr)}
              style={{ padding: '7px 14px', borderRadius: 9, border: 'none',
                background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`,
                color: '#1B1812', fontWeight: 800, fontSize: 11,
                letterSpacing: '0.5px', textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                boxShadow: '0 2px 8px rgba(198,167,90,0.28)' }}>
              <ArrowRight size={11} /> Review
            </button>
            <button data-testid={`milo-proposal-dismiss-${pr.action_id}`}
              onClick={() => onProposalDismiss && onProposalDismiss(pr)}
              style={{ padding: '7px 14px', borderRadius: 9,
                border: `1px solid ${BORDER}`, background: '#FFFFFF',
                color: MUTED, fontWeight: 700, fontSize: 11,
                letterSpacing: '0.5px', textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Dismiss
            </button>
          </div>
          {pr.next_step_hint && (
            <div data-testid={`milo-proposal-next-step-${pr.action_id}`}
              style={{ fontSize: 11, color: MUTED, lineHeight: 1.55,
                fontStyle: 'italic', marginTop: 4,
                paddingTop: 8,
                borderTop: `1px dashed ${BORDER}` }}>
              <strong style={{ color: DARK, fontStyle: 'normal',
                fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
                textTransform: 'uppercase', marginRight: 6 }}>
                Next step
              </strong>
              {pr.next_step_hint}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default MiloConsolePanel;
